import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import api from '../../utils/api';

const OutOfOfficeSettings: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [enabled, setEnabled] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [subject, setSubject] = useState('Out of Office');
  const [message, setMessage] = useState('');
  const [replyTo, setReplyTo] = useState('all');
  const [sendOnce, setSendOnce] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchVacationSettings();
  }, []);

  const fetchVacationSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/mail/vacation');
      
      // Extract the vacation data - it's in response.data.data
      const vacationData = response.data?.data;
      
      // Only set values if we have actual vacation data (not null/undefined)
      if (vacationData && typeof vacationData === 'object' && vacationData.id) {
        setEnabled(vacationData.isEnabled || false);
        setSubject(vacationData.subject || 'Out of Office');
        setMessage(vacationData.htmlBody || vacationData.textBody || '');
        if (vacationData.startDate) {
          setScheduleEnabled(true);
          setStartDate(new Date(vacationData.startDate).toISOString().split('T')[0]);
        }
        if (vacationData.endDate) {
          setEndDate(new Date(vacationData.endDate).toISOString().split('T')[0]);
        }
        setSendOnce(vacationData.replyOncePerSender !== false);
        setReplyTo(vacationData.replyToContactsOnly ? 'contacts' : 'all');
      }
    } catch (error: any) {
      // If no vacation responder exists, use defaults
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await api.post('/mail/vacation', {
        isEnabled: enabled,
        subject,
        htmlBody: message,
        textBody: message.replace(/<[^>]*>/g, ''),
        startDate: scheduleEnabled && startDate ? new Date(startDate).toISOString() : null,
        endDate: scheduleEnabled && endDate ? new Date(endDate).toISOString() : null,
        replyToContactsOnly: replyTo === 'contacts',
        replyOncePerSender: sendOnce,
      });
      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save settings', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 3 }}>
        Out of Office
      </Typography>

      {/* Enable toggle */}
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Enable out of office auto-reply
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Automatically respond to incoming emails when you're away
              </Typography>
            </Box>
          }
        />
      </Box>

      {enabled && (
        <>
          {/* Schedule */}
          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Schedule auto-reply
                </Typography>
              }
            />

            {scheduleEnabled && (
              <Box sx={{ ml: 6, mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Start date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                />
                <TextField
                  label="End date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Auto-reply message */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
              Auto-reply message
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Subject
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line for auto-reply"
                sx={{ maxWidth: 400 }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your out of office message..."
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Reply options */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
              Send reply to
            </Typography>

            <FormControl component="fieldset">
              <RadioGroup
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Everyone</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reply to all incoming emails
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="contacts"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Contacts only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only reply to people in your contacts
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendOnce}
                  onChange={(e) => setSendOnce(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Send only once per sender
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Each sender will only receive one auto-reply during the period
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'warning.light',
              borderRadius: 1,
              borderLeft: '4px solid',
              borderColor: 'warning.main',
              mb: 3,
            }}
          >
            <Typography variant="body2" color="warning.dark">
              Note: Auto-replies will not be sent to mailing lists, spam, or messages sent by automated systems.
            </Typography>
          </Paper>
        </>
      )}

      {/* Save button always visible */}
      <Button 
        variant="contained" 
        color="primary" 
        onClick={handleSave}
        disabled={isSaving}
        startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
      >
        {enabled ? 'Save Settings' : 'Save & Disable Auto-Reply'}
      </Button>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OutOfOfficeSettings;
